/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */


import { Component, Input, ElementRef, Renderer, ViewEncapsulation, HostBinding, Optional, Host, ContentChild } from '@angular/core';
import { NgControl } from '@angular/forms';

import { FormDirective } from './form.directive';
import { FormLabel } from './form.label';
import { FormHelp } from './form.help';
import { Row } from '../grid/row';
import { ColSize } from '../grid/col';
import { isPresent } from '../util/lang';

import classNames from 'classnames';
/**
 * 表单项
 */
export class FormItem extends Row {
/**
 * 样式前缀
 */
prefixCls = 'ant-form';
/**
 * 配合 validateStatus 属性使用，展示校验状态图标，建议只配合 Input 组件使用
 */
hasFeedback = false;
/**
 * label 标签的文本
 */
label: string;
/**
 * 配合 label 属性使用，表示是否显示 label 后面的冒号
 */
colon = true;
/**
 * 标签布局，同 `<Col>` 组件，设置 `span` `offset` 值，如 `{span: 3, offset: 12}` 或 `{sm: {span: 3, offset: 12}}`
 */
labelCol: ColSize | { [key: string]: number | ColSize };
/**
 * 用户CSS样式
 */
labelClass: string;
/**
 * 需要为输入控件设置布局样式时，使用该属性，用法同 labelCol
 */
wrapperCol: ColSize | { [key: string]: number | ColSize };
/**
 * 用户CSS样式
 */
wrapperClass: string;
/**
 * 提示信息，如不设置，则会根据校验规则自动生成
 */
help: string;
/**
 * 额外的提示信息，和 help 类似，当需要错误信息和提示文案同时出现时，可以使用这个。
 */
extra: string;
/**
 * 是否必填项
 */
required = false;

     validateStatus: '' | 'success' | 'warning' | 'error' | 'validating';
/**
 * 用户CSS样式
 */
class: string;
/**
 * 内部输入框
 */
control: NgControl;
/**
 * 用户自定义label
 */
customLabel: FormLabel;
/**
 * 用户自定义help文本
 */
customHelp: FormHelp;
/**
 * @param {?} renderer
 * @param {?} elementRef
 * @param {?} form
 */
constructor(
private renderer: Renderer,
private elementRef: ElementRef,
private form: FormDirective) {
        super(renderer, elementRef);
    }
/**
 * @return {?}
 */
get classes(): string {
        const /** @type {?} */ rowPrefixCls = 'ant-row';
        const /** @type {?} */ rowClassName = {
            [`${rowPrefixCls}`]: !this.type,
            [`${rowPrefixCls}-${this.type}`]: this.type,
            [`${rowPrefixCls}-${this.type}-${this.justify}`]: this.type && this.justify,
            [`${rowPrefixCls}-${this.type}-${this.align}`]: this.type && this.align
        };

        const /** @type {?} */ itemClassName = {
            [`${this.prefixCls}-item`]: true,
            [`${this.prefixCls}-item-with-help`]: this.help,
            [`${this.prefixCls}-item-no-colon`]: !this.colon
        };

        return classNames(rowClassName, itemClassName, this.class);
    }
/**
 * @return {?}
 */
getControlWrapperClasses(): any {
        return {
            [`${this.prefixCls}-item-control-wrapper`]: 1,
            [`${this.wrapperClass}`]: this.wrapperClass
        };
    }
/**
 * @return {?}
 */
getControlClasses(): any {
        const /** @type {?} */ validateStatus = this.getValidateStatus();
        return {
            [`${this.prefixCls}-item-control`]: 1,
            'has-feedback': this.hasFeedback,
            'has-success': validateStatus === 'success',
            'has-warning': validateStatus === 'warning',
            'has-error': validateStatus === 'error',
            'is-validating': validateStatus === 'validating',
        };
    }
/**
 * @return {?}
 */
getLabelWrapperClasses(): any {
        return {
            [`${this.prefixCls}-item-label`]: 1,
            [`${this.labelClass}`]: this.labelClass
        };
    }
/**
 * @return {?}
 */
getLabelClasses(): any {
        return {
            [`${this.prefixCls}-item-required`]: this.isRequired()
        };
    }
/**
 * @return {?}
 */
getLabelText(): string {
        // Keep label is original where there should have no colon
        const /** @type {?} */ haveColon = this.colon && !this.form.isVertical();
        // Remove duplicated user input colon
        if (haveColon && this.label && this.label.trim() !== '') {
            return this.label.replace(/[：|:]\s*$/, '');
        }
        return this.label;
    }
/**
 * @return {?}
 */
getValidateStatus(): string {
        if (isPresent(this.validateStatus) && this.validateStatus !== '') {
            return this.validateStatus;
        }

        const /** @type {?} */ control: NgControl = this.control;
        if (control) {
            const /** @type {?} */ submitted = this.form && this.form.isSubmitted();
            if (control.invalid && (control.touched || submitted)) {
                return 'error';
            }
            if (control.pending) {
                return 'validating';
            }
            if (control.valid && isPresent(control.value) && control.value !== '') {
                return 'success';
            }
        }
        return '';
    }
/**
 * @return {?}
 */
public isRequired(): boolean {
        if (this.required) {
            return true;
        }
        return this.control && this.control.valueAccessor['required'];
    }
/**
 * @param {?} obj
 * @param {?} key
 * @return {?}
 */
private getOwnProperty(obj: any, key: string): any {
        return (obj && obj.hasOwnProperty(key)) ? obj[key] : null;
    }
/**
 * @return {?}
 */
getLabelSpan(): number {
        return /** @type {?} */(( this.getOwnProperty(this.labelCol, 'span') as number));
    }
/**
 * @return {?}
 */
getLabelOffset(): number {
        return /** @type {?} */(( this.getOwnProperty(this.labelCol, 'offset') as number)) || 0;
    }
/**
 * @param {?} size
 * @return {?}
 */
getLabelSize(size: string): any {
        return this.getOwnProperty(this.labelCol, size);
    }
/**
 * @return {?}
 */
getWrapperSpan(): number {
        return /** @type {?} */(( this.getOwnProperty(this.wrapperCol, 'span') as number));
    }
/**
 * @return {?}
 */
getWrapperOffset(): number {
        return /** @type {?} */(( this.getOwnProperty(this.wrapperCol, 'offset') as number)) || 0;
    }
/**
 * @param {?} size
 * @return {?}
 */
getWrapperSize(size: string): any {
        return this.getOwnProperty(this.wrapperCol, size);
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'form-item',
    templateUrl: './form.item.html',
    styleUrls: ['../grid/style/index.scss', './style/index.scss'],
    encapsulation: ViewEncapsulation.None
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
{type: FormDirective, decorators: [{ type: Optional }, { type: Host }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'prefixCls': [{ type: Input },],
'hasFeedback': [{ type: Input },],
'label': [{ type: Input },],
'colon': [{ type: Input },],
'labelCol': [{ type: Input },],
'labelClass': [{ type: Input },],
'wrapperCol': [{ type: Input },],
'wrapperClass': [{ type: Input },],
'help': [{ type: Input },],
'extra': [{ type: Input },],
'required': [{ type: Input },],
'validateStatus': [{ type: Input },],
'class': [{ type: Input },],
'control': [{ type: ContentChild, args: [NgControl, ] },],
'customLabel': [{ type: ContentChild, args: [FormLabel, ] },],
'customHelp': [{ type: ContentChild, args: [FormHelp, ] },],
'classes': [{ type: HostBinding, args: ['class', ] },],
};
}

function FormItem_tsickle_Closure_declarations() {
/** @type {?} */
FormItem.decorators;
/**
 * @nocollapse
 * @type {?}
 */
FormItem.ctorParameters;
/** @type {?} */
FormItem.propDecorators;
/**
 * 样式前缀
 * @type {?}
 */
FormItem.prototype.prefixCls;
/**
 * 配合 validateStatus 属性使用，展示校验状态图标，建议只配合 Input 组件使用
 * @type {?}
 */
FormItem.prototype.hasFeedback;
/**
 * label 标签的文本
 * @type {?}
 */
FormItem.prototype.label;
/**
 * 配合 label 属性使用，表示是否显示 label 后面的冒号
 * @type {?}
 */
FormItem.prototype.colon;
/**
 * 标签布局，同 `<Col>` 组件，设置 `span` `offset` 值，如 `{span: 3, offset: 12}` 或 `{sm: {span: 3, offset: 12}}`
 * @type {?}
 */
FormItem.prototype.labelCol;
/**
 * 用户CSS样式
 * @type {?}
 */
FormItem.prototype.labelClass;
/**
 * 需要为输入控件设置布局样式时，使用该属性，用法同 labelCol
 * @type {?}
 */
FormItem.prototype.wrapperCol;
/**
 * 用户CSS样式
 * @type {?}
 */
FormItem.prototype.wrapperClass;
/**
 * 提示信息，如不设置，则会根据校验规则自动生成
 * @type {?}
 */
FormItem.prototype.help;
/**
 * 额外的提示信息，和 help 类似，当需要错误信息和提示文案同时出现时，可以使用这个。
 * @type {?}
 */
FormItem.prototype.extra;
/**
 * 是否必填项
 * @type {?}
 */
FormItem.prototype.required;
/** @type {?} */
FormItem.prototype.validateStatus;
/**
 * 用户CSS样式
 * @type {?}
 */
FormItem.prototype.class;
/**
 * 内部输入框
 * @type {?}
 */
FormItem.prototype.control;
/**
 * 用户自定义label
 * @type {?}
 */
FormItem.prototype.customLabel;
/**
 * 用户自定义help文本
 * @type {?}
 */
FormItem.prototype.customHelp;
/** @type {?} */
FormItem.prototype.renderer;
/** @type {?} */
FormItem.prototype.elementRef;
/** @type {?} */
FormItem.prototype.form;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
